import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";

export const UserTradesPage = () => {
  const [trades, setTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  // Görev Emri (8, 2026-02) — `date_range` artık backend'e `date_from`/`date_to`
  // olarak GERÇEKTEN gönderiliyor (öncesinde tamamen no-op'tu, ne client-side
  // ne server-side hiçbir filtreleme yapmıyordu). Varsayılan "all" -- mevcut
  // (filtre yokken tüm trade'leri gösteren) davranış KORUNUR; kullanıcı
  // 24h/7d/30d seçtiğinde backend'e canonical date_from/date_to gönderilir.
  // (Görev Emri 8 Düzeltme Turu: "24h" -> "today" olarak yeniden etiketlendi,
  // bkz. dateRangeToParams tanımı.)
  const [filters, setFilters] = useState({ symbol: "", status: "all", strategy: "", side: "all", market_type: "all", date_range: "all" });
  const [detail, setDetail] = useState(null);
  const [detailTimeline, setDetailTimeline] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openOrders, setOpenOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  // Görev Emri (8, 2026-02) — /user/trades 500/503 döndüğünde "0 kayıt"
  // durumunu normal boş veri gibi GÖSTERMEMEK için ayrı hata state'i.
  const [tradesError, setTradesError] = useState(null);
  // P0.3 (2026-06-02): Excel export tarih aralığı state.
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [exportFrom, setExportFrom] = useState(monthAgo);
  const [exportTo, setExportTo] = useState(today);
  const [exporting, setExporting] = useState(false);

  // Görev Emri (8, 2026-02) — canonical date_from/date_to üretimi: hazır
  // seçimler (today/7d/30d) AYNI iki tarihi üretir, backend'in beklediği
  // sözleşmeyle (date_from dahil, date_to seçilen günü tamamen kapsar) birebir
  // uyumlu. "all" -> ikisi de gönderilmez (backend'in mevcut, filtresiz
  // davranışı).
  //
  // Görev Emri (8, Düzeltme Turu, 2026-02) — off-by-one fix: backend
  // sözleşmesi [date_from, date_to+1gün) -- yani "N gün" etiketi TAM N
  // takvim günü kapsamalı. Eskiden "7d" -> days=7 -> kapsanan gün sayısı
  // (today+1)-(today-7) = 8 GÜN idi (1 fazla). Artık "7d" -> days=6 ->
  // (today+1)-(today-6) = TAM 7 gün. Aynı mantık "30d" -> days=29 -> TAM
  // 30 gün için.
  //
  // "24h" etiketi -> "today" olarak değiştirildi (Görev Emri 8, Madde 8):
  // mevcut/backend sözleşmesi tarih-bazlı (gün granülaritesi), rolling
  // 24-saatlik pencere DEĞİL -- "24h" etiketi bunu YANLIŞ ifade ediyordu
  // (gerçek davranış: bugünün UTC takvim günü). Rolling-24h için backend
  // sözleşmesinin datetime/timestamp bazlı genişletilmesi bu düzeltme
  // turunun KAPSAMI DIŞINDA, ayrı backlog maddesi.
  const dateRangeToParams = (rangeKey) => {
    if (rangeKey === "all") return {};
    const days = rangeKey === "today" ? 0 : rangeKey === "7d" ? 6 : 29;
    const from = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    return { date_from: from, date_to: today };
  };

  const load = async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const [tradesRes, openRes, pendingRes] = await Promise.allSettled([
        apiClient.get("/user/trades", { params: { limit: 200, ...dateRangeToParams(filters.date_range) } }),
        apiClient.get("/user/trades/open-orders", { params: { limit: 80 } }),
        apiClient.get("/user/trades/pending-orders", { params: { limit: 80 } }),
      ]);
      if (tradesRes.status === "fulfilled") {
        setTrades(tradesRes.value?.data || []);
        setTradesError(null);
      } else {
        // Görev Emri (8, 2026-02): önceki başarılı yüklemeden kalan veri
        // KORUNUR (kullanıcı en son gördüğü listeyi kaybetmesin) AMA görünür
        // bir hata durumu gösterilir -- "0 kayıt" ile KARIŞTIRILMAZ.
        setTradesError(tradesRes.reason?.response?.data?.detail || "Trade verileri yüklenemedi");
      }
      if (openRes.status === "fulfilled") {
        setOpenOrders(openRes.value?.data || []);
      } else if (!silent) {
        toast.warning("Open orders yüklenemedi, trade listesi gösterilmeye devam ediyor.");
      }
      if (pendingRes.status === "fulfilled") {
        setPendingOrders(pendingRes.value?.data || []);
      } else if (!silent) {
        toast.warning("Pending orders yüklenemedi, trade listesi gösterilmeye devam ediyor.");
      }
      if (tradesRes.status !== "fulfilled" && openRes.status !== "fulfilled" && pendingRes.status !== "fulfilled") {
        throw tradesRes.reason || openRes.reason || pendingRes.reason || new Error("Trades yüklenemedi");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Trades yüklenemedi");
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_range]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load({ silent: true });
    }, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_range]);

  const openTradeDetail = async (trade) => {
    setDetailLoading(true);
    try {
      const [detailRes, timelineRes] = await Promise.allSettled([
        apiClient.get(`/user/trades/${trade.trade_id}`),
        apiClient.get(`/user/trades/${trade.trade_id}/timeline`),
      ]);
      setDetail(detailRes.status === "fulfilled" ? (detailRes.value?.data || null) : null);
      setDetailTimeline(timelineRes.status === "fulfilled" ? (timelineRes.value?.data || null) : null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Trade detail yüklenemedi");
    } finally {
      setDetailLoading(false);
    }
  };

  const formatTradeTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleString("tr-TR");
  };

  const filteredTrades = useMemo(
    () =>
      (trades || []).filter((row) => {
        const symbolOk = !filters.symbol || String(row.symbol || "").toUpperCase().includes(filters.symbol.toUpperCase());
        const statusOk = filters.status === "all" || String(row.status || "").toUpperCase() === filters.status.toUpperCase();
        const strategyOk = !filters.strategy || String(row.strategy || "").toLowerCase().includes(filters.strategy.toLowerCase());
        const sideOk = filters.side === "all" || String(row.side || "").toLowerCase() === filters.side;
        const marketTypeOk = filters.market_type === "all" || String(row.market_type || "spot").toLowerCase() === filters.market_type;
        return symbolOk && statusOk && strategyOk && sideOk && marketTypeOk;
      }),
    [filters, trades],
  );

  const pnlBreakdown = useMemo(() => {
    // 2026-07-21 Görev Emri (9b) — ZORUNLU: `pnl_is_complete === false`
    // (backend pnl_status PENDING/UNKNOWN/MANUAL_REVIEW) satırlar toplama
    // dahil edilmez (Number(null||0)=0 ile "hesaplanamadı" sessizce "gerçekten
    // 0" sayılmasın diye — bkz. Görev 9b kapsam raporu C.9 tablosu).
    const computedRows = filteredTrades.filter((row) => row.pnl_is_complete === true);
    const incompletePnlCount = filteredTrades.filter((row) => row.status === "CLOSED" && row.pnl_is_complete !== true).length;
    const realized = computedRows.reduce((sum, row) => sum + Number(row.realized_pnl || 0), 0);
    const unrealized = filteredTrades.reduce((sum, row) => sum + Number(row.unrealized_pnl || 0), 0);
    const bySymbol = {};
    const byStrategy = {};
    for (const row of computedRows) {
      bySymbol[row.symbol] = (bySymbol[row.symbol] || 0) + Number(row.realized_pnl || 0);
      byStrategy[row.strategy || "unknown"] = (byStrategy[row.strategy || "unknown"] || 0) + Number(row.realized_pnl || 0);
    }
    return { realized, unrealized, total: realized + unrealized, bySymbol, byStrategy, tradeCount: Math.max(filteredTrades.length, openOrders.length), incompletePnlCount };
  }, [filteredTrades, openOrders.length]);

  if (isLoading) {
    return <LoadingSkeleton rows={6} testId="user-trades-loading-skeleton" />;
  }

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="user-trades-page">
      <header className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-trades-header">
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="user-trades-header-controls">
          <div>
            <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-trades-title">Trades</h2>
            <p className="mt-2 text-sm text-slate-400" data-testid="user-trades-description">Mobilde kart görünümü, desktop’ta compact table modu.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCompactMode((previous) => !previous)}
            data-testid="user-trades-compact-mode-toggle"
            aria-label="Compact mode aç/kapat"
          >
            {compactMode ? "Compact: ON" : "Compact: OFF"}
          </Button>
        </div>
        {/* P0.3 (2026-06-02): Excel export tarih aralığı + buton. Max 90 gün. */}
        <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-slate-800 pt-3" data-testid="user-trades-export-bar">
          <div>
            <label htmlFor="export-from" className="block text-xs uppercase tracking-wide text-slate-400">Başlangıç</label>
            <input
              id="export-from"
              type="date"
              value={exportFrom}
              max={exportTo}
              onChange={(e) => setExportFrom(e.target.value)}
              className="mt-1 border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100"
              data-testid="user-trades-export-date-from"
            />
          </div>
          <div>
            <label htmlFor="export-to" className="block text-xs uppercase tracking-wide text-slate-400">Bitiş</label>
            <input
              id="export-to"
              type="date"
              value={exportTo}
              min={exportFrom}
              max={today}
              onChange={(e) => setExportTo(e.target.value)}
              className="mt-1 border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100"
              data-testid="user-trades-export-date-to"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="border-emerald-400 bg-transparent text-emerald-200"
            disabled={exporting}
            onClick={async () => {
              const fromDate = new Date(exportFrom);
              const toDate = new Date(exportTo);
              const diffDays = Math.ceil((toDate - fromDate) / 86400000);
              if (diffDays > 90) {
                toast.error("Max 90 gün seçebilirsiniz");
                return;
              }
              setExporting(true);
              try {
                const response = await apiClient.get("/user/trades/export.xlsx", {
                  params: { date_from: exportFrom, date_to: exportTo },
                  responseType: "blob",
                });
                const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `trades_${exportFrom}_${exportTo}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                const rowCount = response.headers?.["x-trade-row-count"] || "?";
                toast.success(`Excel indirildi (${rowCount} satır)`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Excel export başarısız");
              } finally {
                setExporting(false);
              }
            }}
            data-testid="user-trades-export-xlsx-btn"
          >
            {exporting ? "İndiriliyor..." : "Excel İndir (.xlsx)"}
          </Button>
          <span className="text-xs text-slate-500" data-testid="user-trades-export-helper">Kapanmış trade'ler — max 90 gün</span>
        </div>
      </header>

      <div className="col-span-12 grid gap-3 md:grid-cols-6" data-testid="user-trades-filter-grid">
        <input value={filters.symbol} onChange={(event) => setFilters((prev) => ({ ...prev, symbol: event.target.value }))} placeholder="symbol" className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-symbol-input" />
        <input value={filters.strategy} onChange={(event) => setFilters((prev) => ({ ...prev, strategy: event.target.value }))} placeholder="strategy" className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-strategy-input" />
        <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-status-select"><option value="all">all status</option><option value="OPEN">OPEN</option><option value="CLOSED">CLOSED</option><option value="PENDING">PENDING</option><option value="CANCELLED">CANCELLED</option><option value="REJECTED">REJECTED</option></select>
        <select value={filters.side} onChange={(event) => setFilters((prev) => ({ ...prev, side: event.target.value }))} className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-side-select"><option value="all">all side</option><option value="buy">buy</option><option value="sell">sell</option></select>
        <select value={filters.market_type} onChange={(event) => setFilters((prev) => ({ ...prev, market_type: event.target.value }))} className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-market-type-select"><option value="all">all market</option><option value="spot">spot</option><option value="futures">futures</option></select>
        <select value={filters.date_range} onChange={(event) => setFilters((prev) => ({ ...prev, date_range: event.target.value }))} className="border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-trades-filter-date-select"><option value="all">all time</option><option value="today">today</option><option value="7d">7d</option><option value="30d">30d</option></select>
        {/* 2026-06-02 fix: Filtreleri sıfırla butonu — kullanıcılar "trade görünmüyor" sebebini hızla giderebilsin. */}
        <Button
          type="button"
          variant="outline"
          className="border-slate-500 bg-transparent text-slate-200"
          onClick={() => setFilters({ symbol: "", status: "all", strategy: "", side: "all", market_type: "all", date_range: "all" })}
          data-testid="user-trades-filter-reset-btn"
        >
          Filtreleri Sıfırla
        </Button>
        <span className="text-xs text-slate-500" data-testid="user-trades-filter-debug">
          {trades.length} kayıt yüklendi · {filteredTrades.length} satır görünüyor
        </span>
      </div>

      {tradesError && (
        <div className="col-span-12 rounded border border-rose-700 bg-rose-950/40 p-3 text-sm text-rose-200" data-testid="user-trades-error-banner">
          Trade verileri yüklenemedi: {tradesError}. Aşağıda gösterilen liste (varsa) son başarılı yüklemeden kalan veridir, güncel olmayabilir.
          <Button type="button" variant="outline" className="ml-3 border-rose-400 bg-transparent text-rose-100" onClick={() => load()} data-testid="user-trades-error-retry-btn">
            Tekrar Dene
          </Button>
        </div>
      )}

      <div className="col-span-12 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-trades-pnl-panel">
        <p className="text-xs uppercase tracking-widest text-slate-500">PnL Analysis</p>
        <div className="mt-3 h-48 rounded border border-slate-700 bg-slate-950 p-3 text-sm text-slate-300" data-testid="user-trades-pnl-chart">cumulative pnl: {pnlBreakdown.total.toFixed(4)} | realized={pnlBreakdown.realized.toFixed(4)} | unrealized={pnlBreakdown.unrealized.toFixed(4)}</div>
        <div className="mt-3 grid gap-3 md:grid-cols-6" data-testid="user-trades-pnl-breakdown-grid">
          <div className="border border-slate-700 p-3" data-testid="user-trades-total-pnl-card">total pnl: {pnlBreakdown.total.toFixed(4)}</div>
          <div className="border border-slate-700 p-3" data-testid="user-trades-realized-pnl-card">
            realized pnl: {pnlBreakdown.realized.toFixed(4)}
            {pnlBreakdown.incompletePnlCount > 0 && (
              <div className="mt-1 text-xs text-amber-400" data-testid="user-trades-pnl-incomplete-note">
                {pnlBreakdown.incompletePnlCount} trade PnL'i henüz doğrulanamadı (UNKNOWN) — toplama dahil edilmedi
              </div>
            )}
          </div>
          <div className="border border-slate-700 p-3" data-testid="user-trades-trade-count-card">trade count: {pnlBreakdown.tradeCount}</div>
          <div className="border border-slate-700 p-3" data-testid="user-trades-best-symbol-card">best symbol: {Object.entries(pnlBreakdown.bySymbol).sort((a,b)=>b[1]-a[1])[0]?.[0] || '-'}</div>
          <div className="border border-slate-700 p-3" data-testid="user-trades-worst-symbol-card">worst symbol: {Object.entries(pnlBreakdown.bySymbol).sort((a,b)=>a[1]-b[1])[0]?.[0] || '-'}</div>
          <div className="border border-slate-700 p-3" data-testid="user-trades-top-strategy-card">top strategy: {Object.entries(pnlBreakdown.byStrategy).sort((a,b)=>b[1]-a[1])[0]?.[0] || '-'}</div>
        </div>
      </div>

      <div className="col-span-12 grid gap-3 md:hidden" data-testid="user-trades-mobile-cards" aria-label="Mobil trade kartları">
        {filteredTrades.map((row) => (
          <article key={`${row.source}-${row.trade_id}`} className="rounded border border-slate-800 bg-slate-900 p-3" data-testid={`user-trades-mobile-card-${row.trade_id}`}>
            <p className="text-xs text-slate-500" data-testid={`user-trades-mobile-symbol-${row.trade_id}`}>{row.symbol}</p>
            <p className="text-sm" data-testid={`user-trades-mobile-side-${row.trade_id}`}>{row.side} · {row.status}</p>
            <p className="text-xs text-cyan-300" data-testid={`user-trades-mobile-market-type-${row.trade_id}`}>Market: {String(row.market_type || "spot").toUpperCase()}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-qty-${row.trade_id}`}>Qty: {row.quantity}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-entry-${row.trade_id}`}>Entry: {row.entry_price}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-weight-${row.trade_id}`}>weight: {row.strategy_weight ?? "-"}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-allocation-${row.trade_id}`}>allocation: {row.allocation_source ?? "-"}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-meta-${row.trade_id}`}>meta: {row.meta_engine_decision ?? "-"}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-opened-at-${row.trade_id}`}>Opened: {formatTradeTime(row.opened_at)}</p>
            <p className="text-xs text-slate-400" data-testid={`user-trades-mobile-closed-at-${row.trade_id}`}>Closed: {formatTradeTime(row.closed_at)}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className={`rounded px-2 py-1 text-[11px] ${row.reconciliation_status === 'OK' ? 'bg-emerald-800 text-emerald-200' : row.reconciliation_status === 'PENDING' ? 'bg-amber-800 text-amber-200' : 'bg-rose-800 text-rose-200'}`} data-testid={`user-trades-mobile-reconciliation-${row.trade_id}`}>{row.reconciliation_status}</span>
              <Button variant="outline" onClick={() => openTradeDetail(row)} data-testid={`user-trades-mobile-detail-button-${row.trade_id}`}>Trade Detail</Button>
            </div>
          </article>
        ))}
      </div>

      <div className="col-span-12 hidden overflow-x-auto border border-slate-800 bg-slate-900 md:block" data-testid="user-trades-table-wrapper">
        <table className="min-w-full text-sm" data-testid="user-trades-table" aria-label="Trade tablosu">
          <thead className="bg-slate-800 text-left" data-testid="user-trades-table-head">
            <tr>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-source">Source</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-symbol">Symbol</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-market-type">Market</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-side">Side</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-status">Status</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-qty">Qty</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-entry">Entry</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-pnl">Realized PnL</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-weight">Weight</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-allocation">Allocation</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-meta">Meta</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-opened-at">Opened At</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-closed-at">Closed At</th>
              <th className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid="user-trades-head-trace">Trace</th>
            </tr>
          </thead>
          <tbody data-testid="user-trades-table-body">
            {filteredTrades.map((row) => (
              <tr key={`${row.source}-${row.trade_id}`} className="border-t border-slate-800" data-testid={`user-trades-table-row-${row.trade_id}`}>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-source-${row.trade_id}`}>{row.source}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-symbol-${row.trade_id}`}>{row.symbol}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-market-type-${row.trade_id}`}>{String(row.market_type || "spot").toUpperCase()}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-side-${row.trade_id}`}>{row.side}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-status-${row.trade_id}`}>{row.status}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-quantity-${row.trade_id}`}>{row.quantity}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-entry-${row.trade_id}`}>{row.entry_price}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-realized-pnl-${row.trade_id}`}>
                  {row.realized_pnl ?? "-"}
                  {row.pnl_status && row.pnl_status !== "COMPUTED" && (
                    <span className="ml-1 text-xs text-amber-400" data-testid={`user-trades-row-pnl-status-${row.trade_id}`}>({row.pnl_status})</span>
                  )}
                </td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-weight-${row.trade_id}`}>{row.strategy_weight ?? "-"}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-allocation-${row.trade_id}`}>{row.allocation_source ?? "-"}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-meta-${row.trade_id}`}>{row.meta_engine_decision ?? "-"}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-opened-at-${row.trade_id}`}>{formatTradeTime(row.opened_at)}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"} data-testid={`user-trades-row-closed-at-${row.trade_id}`}>{formatTradeTime(row.closed_at)}</td>
                <td className={compactMode ? "px-2 py-1" : "px-3 py-2"}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-1 text-[11px] ${row.reconciliation_status === 'OK' ? 'bg-emerald-800 text-emerald-200' : row.reconciliation_status === 'PENDING' ? 'bg-amber-800 text-amber-200' : 'bg-rose-800 text-rose-200'}`} data-testid={`user-trades-reconciliation-badge-${row.trade_id}`}>{row.reconciliation_status}</span>
                    <Button variant="outline" onClick={() => openTradeDetail(row)} data-testid={`user-trades-detail-button-${row.trade_id}`}>Trade Detail</Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredTrades.length === 0 && (
              <tr data-testid="user-trades-empty-row">
                <td colSpan={14} className="px-3 py-8 text-center text-slate-400" data-testid="user-trades-empty-cell">
                  Trade verisi şu anda görünmüyor. Ana liste boşsa bile open/pending panelleri bağımsız yüklenir; filtreleri sıfırlayıp tekrar deneyin.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="col-span-12 grid gap-4 lg:grid-cols-2" data-testid="user-trades-open-pending-grid">
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="user-trades-open-orders-panel">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold">Open Orders</h3>
            <span className="text-xs text-slate-400" data-testid="user-trades-open-orders-count">{openOrders.length} kayıt</span>
          </div>
          <pre className="mt-3 max-h-64 overflow-auto bg-slate-950 p-3 text-xs text-slate-300">{JSON.stringify(openOrders, null, 2)}</pre>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="user-trades-pending-orders-panel">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold">Pending Orders</h3>
            <span className="text-xs text-slate-400" data-testid="user-trades-pending-orders-count">{pendingOrders.length} kayıt</span>
          </div>
          <pre className="mt-3 max-h-64 overflow-auto bg-slate-950 p-3 text-xs text-slate-300">{JSON.stringify(pendingOrders, null, 2)}</pre>
        </article>
      </div>

      <aside className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-trades-detail-side-panel">
        <p className="text-xs uppercase tracking-widest text-slate-500">Trade Detail / Replay</p>
        {!detail && <p className="mt-2 text-sm text-slate-400" data-testid="user-trades-detail-empty">Bir trade seçerek lifecycle detayını açın.</p>}
        {detailLoading && <p className="mt-2 text-sm text-slate-300" data-testid="user-trades-detail-loading">Trade detail yükleniyor...</p>}
        {detail && (
          <div className="mt-3 space-y-4" data-testid="user-trades-detail-content">
            <Link to={`/user/trades/${detail.trade.trade_id}`} className="inline-flex underline text-cyan-300" data-testid="user-trades-open-detail-page-link">Open full detail page</Link>
            <div className="grid gap-2 md:grid-cols-4" data-testid="user-trades-detail-replay-summary-grid">
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-replay-entry">Entry: {detail.trade?.avg_fill_price ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-replay-realized">Realized: {detail.trade?.realized_pnl ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-replay-unrealized">Unrealized: {detail.trade?.unrealized_pnl ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-replay-status">Status: {detail.trade?.status ?? '-'}</div>
            </div>
            {/* Görev Emri (2026-09-14, Revizyon Turu 3) Madde 10 (P1) — P0
                kabul engeli FİX: gross/net PnL ve komisyon kırılımı Revizyon
                2'de SADECE backend API/export'a taşınmıştı, frontend'de HİÇ
                gösterilmiyordu (kullanıcı bulgusu: "net_realized_pnl'i
                frontend'e taşı"). `net_realized_pnl`, `pnl_status !=
                'COMPUTED'` iken backend'den `null` gelir — bu durumda
                "Doğrulanmadı" gösterilir, ASLA 0 veya ham realized_pnl'e
                sessizce düşülmez (Görev 9b'nin "kanıtsız 0 ile karıştırma"
                ilkesiyle tutarlı). */}
            <div className="grid gap-2 md:grid-cols-3" data-testid="user-trades-detail-pnl-breakdown-grid">
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-net-realized-pnl">
                Net Realized PnL: {detail.trade?.pnl_status === 'COMPUTED' ? (detail.trade?.net_realized_pnl ?? '-') : 'Doğrulanmadı'}
                <span className="ml-1 text-[10px] text-slate-500">(kanonik/net — türetmeyin)</span>
              </div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-gross-realized-pnl">
                Gross Realized PnL: {detail.trade?.pnl_status === 'COMPUTED' ? (detail.trade?.gross_realized_pnl ?? '-') : 'Doğrulanmadı'}
              </div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-pnl-status">PnL Status: {detail.trade?.pnl_status || '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-entry-commission">Entry Commission: {detail.trade?.entry_commission ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-exit-commission">Exit Commission: {detail.trade?.exit_commission ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-commission-paid">Commission Paid: {detail.trade?.commission_paid ?? '-'}</div>
              <div className="border border-slate-700 p-3" data-testid="user-trades-detail-funding-fee">Funding Fee: {detail.trade?.funding_fee ?? '-'}</div>
            </div>
            <div data-testid="user-trades-detail-timeline-panel">
              <p className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-trades-detail-timeline-title">Exit Timeline</p>
              <div className="mt-3 space-y-3 border-l border-slate-700 pl-4" data-testid="user-trades-detail-timeline-steps">
                {((detailTimeline?.timeline || detail?.timeline || [])).map((item, index) => (
                  <div key={`${item.event}-${index}`} className="relative" data-testid={`user-trades-detail-timeline-step-${index}`}>
                    <span className="absolute -left-[21px] top-1 h-3 w-3 rounded-full border border-cyan-400 bg-slate-950" />
                    <p className="text-sm font-semibold text-slate-100" data-testid={`user-trades-detail-timeline-event-${index}`}>{item.event}</p>
                    <p className="text-xs text-slate-400" data-testid={`user-trades-detail-timeline-at-${index}`}>{formatTradeTime(item.at)}</p>
                  </div>
                ))}
                {((detailTimeline?.missing_steps || []).length > 0) && (
                  <div className="rounded border border-amber-600/50 bg-amber-950/20 p-3" data-testid="user-trades-detail-timeline-missing-steps">
                    <p className="text-xs uppercase tracking-widest text-amber-300">Eksik Event</p>
                    <p className="mt-1 text-xs text-amber-100">{detailTimeline.missing_steps.join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </aside>
    </section>
  );
};